import React, { useState } from 'react';
import './LandmarkZoneModal.css';

/**
 * Modal shown when user tries to place a memory in a landmark's premium zone
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Handler to close modal
 * @param {Function} props.onConfirm - Handler when user confirms payment
 * @param {Function} props.onBuyCredits - Handler to open credits modal
 * @param {Function} props.onOpenAuth - Handler to open auth modal for sign-in
 * @param {Object} props.user - Firebase user object (null if anonymous)
 * @param {number|null} props.currentCredits - User's current credit/gem balance
 * @param {Object} props.zoneInfo - Info about the landmark zone
 * @param {Object} props.zoneInfo.landmark - Landmark details
 * @param {number} props.zoneInfo.gemCost - Cost in gems
 * @param {number} props.zoneInfo.distance - Distance to landmark in meters
 */
export default function LandmarkZoneModal({
  isOpen,
  onClose,
  onConfirm,
  onBuyCredits,
  onOpenAuth,
  user,
  currentCredits,
  zoneInfo,
}) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!user || !zoneInfo) return;

    setLoading(true);
    try {
      await onConfirm?.();
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleBuyCredits = () => {
    onClose();
    onBuyCredits?.();
  };

  const handleOpenAuth = () => {
    onClose();
    onOpenAuth?.();
  };

  if (!isOpen || !zoneInfo) return null;

  const credits = currentCredits ?? 0;
  const canAfford = credits >= zoneInfo.gemCost;
  const isSignedIn = !!user;

  const { landmark, gemCost, distance } = zoneInfo;

  // Rarity badge styling
  const rarityStyles = {
    legendary: {
      background: 'linear-gradient(135deg, #FFD700 0%, #F2C6A0 100%)',
      color: '#8B6914',
    },
    epic: {
      background: 'linear-gradient(135deg, #9370DB 0%, #B8A0C4 100%)',
      color: '#4B0082',
    },
    rare: {
      background: 'linear-gradient(135deg, #4169E1 0%, #6495ED 100%)',
      color: '#1E3A5F',
    },
  };

  const badgeStyle = rarityStyles[landmark.rarity] || rarityStyles.rare;

  return (
    <div className="landmark-zone-overlay" onClick={handleOverlayClick}>
      <div className="landmark-zone-modal">
        <button
          className="landmark-zone-close"
          onClick={onClose}
          aria-label="Close"
          disabled={loading}
        >
          <span aria-hidden="true">&times;</span>
        </button>

        {/* Landmark emoji */}
        <div className="landmark-zone-emoji">{landmark.emoji}</div>

        {/* Header */}
        <div className="landmark-zone-header">
          <h2>Premium Zone</h2>
          <p className="landmark-zone-name">{landmark.name}</p>
          <span
            className="landmark-zone-rarity-badge"
            style={badgeStyle}
          >
            {landmark.rarity === 'legendary' && '⭐ '}
            {landmark.rarity === 'epic' && '💎 '}
            {landmark.rarity === 'rare' && '✨ '}
            {landmark.rarity.charAt(0).toUpperCase() + landmark.rarity.slice(1)}
          </span>
        </div>

        {/* Explanation */}
        <p className="landmark-zone-explanation">
          A guaranteed spot in this landmark zone costs gems and uses one of your memory slots.
        </p>

        {/* Cost display */}
        <div className="landmark-zone-cost">
          <div className="landmark-zone-cost-gems">
            <span className="landmark-zone-cost-icon">💎</span>
            <span className="landmark-zone-cost-amount">{gemCost} gems</span>
          </div>
        </div>

        {/* Current balance (for signed-in users) */}
        {isSignedIn && (
          <p className="landmark-zone-balance">
            Your gems: <strong>{credits}</strong>
          </p>
        )}

        {/* Action buttons */}
        <div className="landmark-zone-actions">
          {!isSignedIn ? (
            // Not signed in - prompt to sign in
            <>
              <button
                className="landmark-zone-action-primary"
                onClick={handleOpenAuth}
              >
                Sign in to leave a memory here
              </button>
              <button
                className="landmark-zone-action-secondary"
                onClick={onClose}
              >
                Choose Another Location
              </button>
            </>
          ) : canAfford ? (
            // Signed in with enough gems
            <>
              <button
                className="landmark-zone-action-primary"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="landmark-zone-spinner"></span>
                    Processing...
                  </>
                ) : (
                  `Pay ${gemCost} gems (guaranteed)`
                )}
              </button>
              <button
                className="landmark-zone-action-tertiary"
                onClick={onClose}
                disabled={loading}
              >
                Choose Another Location
              </button>
            </>
          ) : (
            // Signed in but not enough gems
            <>
              <button
                className="landmark-zone-action-secondary"
                onClick={handleBuyCredits}
              >
                Buy More Gems
              </button>
              <button
                className="landmark-zone-action-tertiary"
                onClick={onClose}
              >
                Choose Another Location
              </button>
            </>
          )}
        </div>

        {/* Distance hint */}
        <p className="landmark-zone-distance">
          You're {distance}m from {landmark.name}
        </p>
      </div>
    </div>
  );
}
